import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  sendPasswordResetEmail,
  updateProfile,
  User,
  sendEmailVerification,
  onAuthStateChanged,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  OAuthProvider,
  linkWithPopup,
  linkWithRedirect,
  unlink
} from 'firebase/auth';
import { auth, googleProvider, facebookProvider, twitterProvider } from './config';
import { createUserProfile, getUserProfile, updateUserProfileData } from './firestoreService';

export interface UserData {
  permissions: any;
  uid: string;
  email: string;
  displayName?: string;
  role: 'super_admin' | 'admin' | 'user';
  firstName: string;
  lastName: string;
  phone?: string;
  dateOfBirth?: string;
  gender?: 'male' | 'female' | 'other' | 'prefer-not-to-say';
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  profileImage?: string;
  isActive: boolean;
  emailVerified: boolean;
  createdAt: Date;
  updatedAt: Date;
  lastLoginAt?: Date;
  provider?: string[];
  termsAccepted?: boolean;
  termsAcceptedAt?: Date;
  marketingEmails?: boolean;
}


// Google Sign-In
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    
    // Check if user exists in Firestore
    const userProfile = await getUserProfile(user.uid);
    
    if (!userProfile.success || !userProfile.data) {
      // Create new user profile for Google sign-in
      const nameParts = user.displayName?.split(' ') || ['User', ''];
      
      const newUserProfile: UserData = {
        uid: user.uid,
        email: user.email!,
        displayName: user.displayName || '',
        role: 'user',
        firstName: nameParts[0],
        lastName: nameParts.slice(1).join(' ') || 'User',
        phone: '',
        isActive: true,
        emailVerified: user.emailVerified,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastLoginAt: new Date(),
        provider: ['google'],
        termsAccepted: true,
        termsAcceptedAt: new Date(),
        marketingEmails: true,
        permissions: undefined
      };
      
      await createUserProfile(user.uid, newUserProfile);
    } else {
      // Update last login time for existing user
      await updateUserProfileData(user.uid, {
        lastLoginAt: new Date(),
        updatedAt: new Date(),
        provider: Array.from(new Set([...(userProfile.data.provider || []), 'google']))
      });
    }
    
    return { 
      success: true, 
      user: {
        ...user,
        role: userProfile.success && userProfile.data ? userProfile.data.role : 'user'
      }
    };
  } catch (error: any) {
    console.error('Google Sign-In error:', error);
    return { 
      success: false, 
      error: error.message || 'Google Sign-In failed'
    };
  }
};


// Link Google account to existing user
export const linkGoogleAccount = async (user: User) => {
  try {
    const result = await linkWithPopup(user, googleProvider);
    await updateUserProfileData(user.uid, {
      provider: Array.from(new Set([...(await getUserProfile(user.uid)).data?.provider || [], 'google']))
    });
    return { success: true, user: result.user };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};


// Enhanced Register function
export const registerUser = async (
  email: string, 
  password: string, 
  userData: {
    firstName: string;
    lastName: string;
    phone?: string;
    dateOfBirth?: string;
    gender?: 'male' | 'female' | 'other' | 'prefer-not-to-say';
    role?: 'user' | 'admin';
    termsAccepted?: boolean;
    marketingEmails?: boolean;
  }
) => {
  try {
    // Age validation
    if (userData.dateOfBirth) {
      const birthDate = new Date(userData.dateOfBirth);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      if (age < 18) {
        throw new Error('You must be at least 18 years old to register');
      }
      
      if (age < 18 && userData.role !== 'user') {
        throw new Error('Admin/Super Admin must be at least 18 years old');
      }
    }

    // Terms validation
    if (!userData.termsAccepted) {
      throw new Error('You must accept the terms and conditions');
    }

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Update Firebase Auth profile
    await updateProfile(user, {
      displayName: `${userData.firstName} ${userData.lastName}`
    });

    // Send email verification
    await sendEmailVerification(user);

    // Create user profile in Firestore
    const userProfile: UserData = {
      uid: user.uid,
      email: user.email!,
      displayName: `${userData.firstName} ${userData.lastName}`,
      role: userData.role || 'user',
      firstName: userData.firstName,
      lastName: userData.lastName,
      phone: userData.phone || '',
      dateOfBirth: userData.dateOfBirth || '',
      gender: userData.gender || 'prefer-not-to-say',
      isActive: true,
      emailVerified: false,
      createdAt: new Date(),
      updatedAt: new Date(),
      termsAccepted: userData.termsAccepted,
      termsAcceptedAt: new Date(),
      marketingEmails: userData.marketingEmails || false,
      provider: ['email'],
      permissions: undefined
    };

    await createUserProfile(user.uid, userProfile);

    return { 
      success: true, 
      user: user,
      message: 'Registration successful! Please check your email to verify your account.'
    };
  } catch (error: any) {
    console.error('Registration error:', error);
    
    // Handle specific error cases
    let errorMessage = error.message || 'Registration failed';
    if (error.code === 'auth/email-already-in-use') {
      errorMessage = 'This email is already registered. Please try logging in or use a different email.';
    } else if (error.code === 'auth/weak-password') {
      errorMessage = 'Password should be at least 6 characters.';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address.';
    }
    
    return { 
      success: false, 
      error: errorMessage
    };
  }
};


// Enhanced login function
export const loginUser = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;
    
    // Check if email is verified
    if (!user.emailVerified) {
      // Allow login but show warning
      console.warn('Email not verified');
    }
    
    // Update last login time
    await updateUserProfileData(user.uid, {
      lastLoginAt: new Date(),
      updatedAt: new Date()
    });

    // Get user profile
    const userProfile = await getUserProfile(user.uid);
    
    // Check if account is active
    if (userProfile.success && userProfile.data && !userProfile.data.isActive) {
      await signOut(auth);
      throw new Error('Your account has been deactivated. Please contact support.');
    }
    
    return { 
      success: true, 
      user: {
        ...user,
        role: userProfile.success ? userProfile.data?.role : 'user'
      }
    };
  } catch (error: any) {
    console.error('Login error:', error);
    
    let errorMessage = error.message || 'Login failed';
    if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email.';
    } else if (error.code === 'auth/wrong-password') {
      errorMessage = 'Incorrect password.';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many failed attempts. Please try again later or reset your password.';
    } else if (error.code === 'auth/user-disabled') {
      errorMessage = 'This account has been disabled. Please contact support.';
    }
    
    return { 
      success: false, 
      error: errorMessage
    };
  }
};

// Get user with profile data
export const getUserWithProfile = async (userId: string) => {
  try {
    const userProfile = await getUserProfile(userId);
    if (userProfile.success && userProfile.data) {
      return {
        success: true,
        user: userProfile.data
      };
    }
    return { success: false, error: 'User profile not found' };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Verify email (resend)
export const resendVerificationEmail = async (user: User) => {
  try {
    await sendEmailVerification(user);
    return { success: true, message: 'Verification email sent!' };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Check if user exists by email
export const checkUserExists = async (email: string) => {
  try {
    // This is a Firestore check since Firebase Auth doesn't have a direct method
    // You might need to implement a cloud function or maintain a separate index
    return { success: true, exists: false }; // Placeholder
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Logout user
export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Reset password
export const resetPassword = async (email: string) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Update user profile
export const updateUserProfile = async (user: User, data: any) => {
  try {
    await updateProfile(user, data);
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Change password
export const changePassword = async (user: User, currentPassword: string, newPassword: string) => {
  try {
    // Re-authenticate user
    const credential = EmailAuthProvider.credential(user.email!, currentPassword);
    await reauthenticateWithCredential(user, credential);
    
    // Update password
    await updatePassword(user, newPassword);
    
    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message };
  }
};

// Check if user has required role
export const hasRole = (userRole: string, requiredRole: string) => {
  const roleHierarchy = {
    'super_admin': ['super_admin', 'admin', 'user'],
    'admin': ['admin', 'user'],
    'user': ['user']
  };
  
  return roleHierarchy[userRole as keyof typeof roleHierarchy]?.includes(requiredRole) || false;
};

// Auth state listener
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Get current user with role
export const getCurrentUser = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  
  const userProfile = await getUserProfile(user.uid);
  return {
    ...user,
    role: userProfile.success ? userProfile.data?.role : 'user'
  };
};